<template>
  <ux-molecules-inspiration-heading
    :current-page="currentPage"
    :subtitle="subtitle"
    :title="title"
    :total-pages="totalPages"
    class="mb-5"
    v-if="title"
  />
  <section class="Grid-layout">
    <ux-molecules-inspiration-filter
      :filters="filters"
      :filters-title="$t('pages.inspiration.filterByType')"
      :label="$t('pages.inspiration.types')"
      @inspiration-filter::filter-update="filterUpdate"
    />
  </section>

  <section class="Layout">
    <ux-molecules-inspiration-editorial-card
      :alt="lastEditorialContent?.coverImage?.alt"
      :description="lastEditorialContent?.subtitle"
      :href="`${lastEditorialContent?.variant?.toLocaleLowerCase()}/${lastEditorialContent?.slug}`"
      :label="$t(`ux.molecules.inspirationFilterContent.${lastEditorialContent?.variant?.toLocaleLowerCase()}`) ?? ''"
      :src="smallSrc(lastEditorialContent?.coverImage?.src, lastEditorialContent?.coverImage?.srcSet)"
      :src-set="lastEditorialContent?.coverImage?.srcSet"
      :title="lastEditorialContent?.title"
    />
  </section>

  <section v-if="cards?.length">
    <ux-molecules-grid-card-inspiration :cards="cards.slice(0, 3)" :columns="3" key="row-1" />
    <ux-molecules-grid-card-inspiration :cards="cards.slice(3, 5)" :columns="2" key="row-2" v-if="cards.length > 3" />
    <ux-molecules-grid-card-inspiration :cards="cards.slice(5, 8)" :columns="3" key="row-3" v-if="cards.length > 5" />
  </section>
  <section class="Layout" v-if="totalPages > 1">
    <ux-molecules-inspiration-pagination
      :current-page="currentPage"
      :key="currentPage"
      :total-page="totalPages"
      @inspiration-pagination::go-to-page="goToPage"
      @inspiration-pagination::next="goToNext"
      @inspiration-pagination::prev="goToPrev"
    />
  </section>
  <section class="Layout" v-if="selectedLoyaltyBanner?.title">
    <ux-molecules-loyalty-banner
      :button-label="!userStore.isLogged ? $t('ux.molecules.navAccountDropdown.login') : selectedLoyaltyBanner?.ctaLabel"
      :button-link="!userStore.isLogged ? loginLink : selectedLoyaltyBanner?.ctaUrl"
      :external="userStore.isLogged"
      :secondary-button-label="!userStore.isLogged ? selectedLoyaltyBanner?.ctaLabel : null"
      :secondary-button-link="!userStore.isLogged ? selectedLoyaltyBanner?.ctaUrl : null"
      :subtitle="selectedLoyaltyBanner?.subtitle"
      :title="selectedLoyaltyBanner.title"
      logo="logo-all"
    />
  </section>
</template>
<script lang="ts" setup>
import { FiltersContent } from '~/components/ux/molecules/InspirationFilterContent/InspirationFilterContent.vue';
import { LoyaltyProgram, LoyaltyType, LoyaltyUserStatus } from '~/core';
import {
  EditorialContent,
  SearchEditorialContentQueryVariables,
  useInspirationAsyncData,
  useLoyaltyBannersAsyncData,
  useSearchEditorialContentAsyncData,
  useSearchEditorialContentLazyQuery
} from '~/graphql';
import { capitalizeFirstLetter } from '~/helpers/capitaliseFirstLetter';
import { gtmPageAndUser } from '~/helpers/gtm';
import { smallSrc } from '~/helpers/imageSizes';

const { t } = useI18n();
const { getAuthUrl } = useAuth();

const setI18nParams = useSetI18nParams();

setI18nParams({
  en: { inspiration: 'inspiration' },
  fr: { inspiration: 'inspiration' }
});

const size = ref<number>(8);
const filters = ref<FiltersContent[]>([
  { id: 'offer', label: t(`ux.molecules.inspirationFilterContent.offer`), value: false },
  { id: 'collection', label: t(`ux.molecules.inspirationFilterContent.collection`), value: false }
]);

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const currentPage = computed({
  get: () => Number(route.query.page) || 1,
  set: (value) => value
});

const variables = computed(() => ({
  from: (currentPage.value - 1) * size.value,
  size: size.value,
  variant: filters.value.filter((filter) => filter.value).map((filter) => capitalizeFirstLetter(filter.id))
}));

const loginLink = computed(() => {
  return getAuthUrl('login');
});

const { data: LoyaltyBannerData } = await useLoyaltyBannersAsyncData({
  type: LoyaltyType.Home
});

const loyaltyBanners = computed(() => LoyaltyBannerData.value?.loyaltyBanner);

const selectedLoyaltyBanner = computed(() =>
  loyaltyBanners.value?.find(
    (loyaltyBanner) =>
      (userStore.isLogged ? loyaltyBanner?.userStatus === LoyaltyUserStatus.LOGGED_IN : loyaltyBanner?.userStatus === LoyaltyUserStatus.LOGGED_OUT) &&
      loyaltyBanner?.type === LoyaltyType.Editorial &&
      loyaltyBanner?.program === LoyaltyProgram.All
  )
);

const { data } = useInspirationAsyncData();

const { data: searchEditorialContentData } = useSearchEditorialContentAsyncData({
  ...variables.value
});

const { load, onResult } = useSearchEditorialContentLazyQuery();

const inspiration = computed(() => data.value?.inspiration);

const title = computed(() => inspiration.value?.title);

const subtitle = computed(() => inspiration.value?.subtitle);

const cards = computed<EditorialContent[]>(() => searchEditorialContentData?.value?.searchEditorialContent?.results as EditorialContent[]);

const lastEditorialContent = computed(() => searchEditorialContentData.value?.searchEditorialContent?.lastEditorialContent);

const totalPages = computed<number>(() => searchEditorialContentData?.value?.searchEditorialContent?.totalPage as number);

const goToPrev = () => currentPage.value--;
const goToNext = () => currentPage.value++;
const goToPage = (page: number) => (currentPage.value = page);

const filterUpdate = async (filtersUpdated: FiltersContent[]) => {
  filters.value = filtersUpdated;

  const variant = filtersUpdated.filter((filter) => filter.value).map((filter) => capitalizeFirstLetter(filter.id));

  await handleUpdateEditorialContent({ ...variables.value, variant });
};

const handleUpdateEditorialContent = async (variables: SearchEditorialContentQueryVariables) => {
  onResult((response) => {
    if (response.loading) return;

    if (response.data.searchEditorialContent) {
      searchEditorialContentData.value = response.data;
    }
  });

  await load(undefined, { ...variables });
};

const pageEvent = () => {
  gtmPageAndUser({
    brandcontext: 'restaurantsandbars',
    category: 'restaurantsandbars',
    countrymarket: 'fr',
    currencycode: 'EUR',
    daysbeforearrival: undefined, // cannot be found here
    dynamic_datas_ready: true,
    event: 'push_dynamic_datas',
    pagename: 'restaurantsandbars::inspiration::landing',
    pagetype: 'restaurantsandbars',
    sub_category_level1: 'inspiration',
    sub_category_level2: 'landing'
  });
};

whenever(
  () => userStore.userLoadDone,
  () => {
    pageEvent();
  }
);

onMounted(() => {
  if (userStore.userLoadDone) {
    pageEvent();
  }
});

watch(
  () => currentPage,
  async () => {
    await handleUpdateEditorialContent({ ...variables.value, from: (currentPage.value - 1) * size.value });
  },
  { deep: true }
);

onMounted(async () => {
  if (currentPage.value >= totalPages.value) {
    currentPage.value = totalPages.value;
    await router.push({
      query: {
        page: totalPages.value
      }
    });
  }
});

useHead(() => ({
  meta: [
    {
      content: inspiration.value?.metaDescription ?? inspiration.value?.subtitle,
      name: 'description'
    }
  ],
  title: inspiration.value?.metaTitle ?? inspiration.value?.title
}));
</script>
